import { CardapioItem } from "../types/cardapio";

export const removeSaborFractionPrefix = (value: string): string =>
  value.replace(/^\s*(?:\d+\s*\/\s*\d+|meia|metade)\s*[-–—:]?\s*/i, "").trim();

/** Remove canal Foodfly `(Doce) -` e prefixo `Broto`. */
export const stripSaborCatalogPrefix = (value: string): string =>
  removeSaborFractionPrefix(value)
    .replace(/^\([^)]{0,120}\)\s*-\s*/i, "")
    .replace(/^\s*broto\s+/i, "")
    .trim();

export const normalizeSaborKey = (value: string): string =>
  stripSaborCatalogPrefix(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();

export const compactSaborKey = (value: string): string =>
  normalizeSaborKey(value).replace(/[^A-Z0-9]+/g, "");

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  if (Math.abs(a.length - b.length) > 1) return 2;

  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j += 1) prev[j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j += 1) prev[j] = curr[j];
  }
  return prev[b.length];
}

function preferCatalogItem(a: CardapioItem, b: CardapioItem): CardapioItem {
  const aBroto = /^\s*broto\s+/i.test(a.nome) ? 1 : 0;
  const bBroto = /^\s*broto\s+/i.test(b.nome) ? 1 : 0;
  if (aBroto !== bBroto) return aBroto < bBroto ? a : b;
  return a.nome.length <= b.nome.length ? a : b;
}

export function buildCardapioSaboresMap(
  sabores: readonly CardapioItem[],
): Map<string, CardapioItem> {
  const map = new Map<string, CardapioItem>();
  const ordered = [...sabores].sort((a, b) => {
    const aBroto = /^\s*broto\s+/i.test(a.nome) ? 1 : 0;
    const bBroto = /^\s*broto\s+/i.test(b.nome) ? 1 : 0;
    return aBroto - bBroto;
  });
  for (const item of ordered) {
    const keys = [
      normalizeSaborKey(item.nome),
      compactSaborKey(item.nome),
    ];
    for (const key of keys) {
      if (!key) continue;
      const existing = map.get(key);
      map.set(key, existing ? preferCatalogItem(existing, item) : item);
    }
  }
  return map;
}

function uniqueCompactNearMatch(
  compact: string,
  saboresCardapio: readonly CardapioItem[],
): CardapioItem | null {
  if (compact.length < 4) return null;
  const hits: CardapioItem[] = [];
  const seenIds = new Set<number>();
  for (const item of saboresCardapio) {
    const itemCompact = compactSaborKey(item.nome);
    if (!itemCompact || levenshtein(compact, itemCompact) !== 1) continue;
    if (seenIds.has(item.id)) continue;
    seenIds.add(item.id);
    hits.push(item);
  }
  if (hits.length !== 1) return null;
  return hits[0];
}

/**
 * Associa o nome do sabor no pedido ao cadastro do cardápio.
 * 1) Match exato (nome normalizado, sem canal/broto)
 * 2) Match compacto (ignora espaços: CHEESECAKE → CHEESE CAKE)
 * 3) Typo de 1 letra, se o cadastro for único (CANADENCE → CANADENSE)
 * 4) Fallback: nome do cardápio contém o sabor do pedido por inteiro
 */
export function resolveCardapioSabor(
  saborSemFracao: string,
  saboresCardapio: readonly CardapioItem[],
  exactMap?: Map<string, CardapioItem>,
): CardapioItem | null {
  const map = exactMap ?? buildCardapioSaboresMap(saboresCardapio);
  const saborKey = normalizeSaborKey(saborSemFracao);
  if (!saborKey) return null;

  const exact = map.get(saborKey);
  if (exact) return exact;

  const compact = compactSaborKey(saborSemFracao);
  if (compact.length >= 2) {
    const byCompact = map.get(compact);
    if (byCompact) return byCompact;
    const near = uniqueCompactNearMatch(compact, saboresCardapio);
    if (near) return near;
  }

  let best: CardapioItem | null = null;
  let bestKeyLen = -1;
  for (const item of saboresCardapio) {
    const cardapioKey = normalizeSaborKey(item.nome);
    if (!cardapioKey || cardapioKey.length < saborKey.length) continue;
    if (!cardapioKey.includes(saborKey)) continue;
    if (cardapioKey.length > bestKeyLen) {
      bestKeyLen = cardapioKey.length;
      best = item;
    }
  }
  return best;
}
